import { PersonList } from '@insurance/api/requests'
import {
  AddPersonButton,
  CountrySelect,
  InputDatePicker
} from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { Col, Divider, Form, Input, Row, Spin, Typography } from 'antd'
import dayjs from 'dayjs'
import { Add, UserAdd } from 'iconsax-react'
import { useTranslation } from 'react-i18next'

import { CoefficientInput } from './CoefficientInput'
import { useFinancialInsuranceValues } from './FinancialInsuranceContext'
import { FinancialPersonFormValue } from './type'
import { formProps, MIN_AGE } from './utils'
import { ContractorTypeFields, EditPersonsWrapper } from '../PersonsFields'

const countIdnpMatches = (persons: FinancialPersonFormValue[], idnp: string) =>
  persons.filter(
    (person) =>
      !!person?.identification_number &&
      String(person.identification_number).trim() === idnp
  ).length

const mapExistingPerson = (person: PersonList): FinancialPersonFormValue => ({
  id: person.id,
  first_name: person.first_name,
  last_name: person.last_name,
  identification_number: person.identification_number,
  date_of_birth: dayjs(person.date_of_birth).isValid()
    ? dayjs(person.date_of_birth)
    : undefined,
  document: [person.document?.series, person.document?.number]
    .filter(Boolean)
    .join('')
})

export const InformationStep = () => {
  const { t } = useTranslation()
  const { required, fullName, personalCode, passportSeriesNumber } =
    useValidationsField()
  const { personsProps, personsCountProps, territoryProps } = formProps(t)
  const { isCalculatingLoading, handleRecalculate } =
    useFinancialInsuranceValues()

  const formInstance = Form.useFormInstance()
  const persons =
    (Form.useWatch(personsProps.name, {
      preserve: true
    }) as FinancialPersonFormValue[]) || []

  const syncPersonsCountAndRecalculate = () => {
    const currentPersons = formInstance.getFieldValue(
      personsProps.name
    ) as FinancialPersonFormValue[]

    formInstance.setFieldValue(
      personsCountProps.name,
      currentPersons?.length || 1
    )
    handleRecalculate()
  }

  const handleReplacePerson = (name: number, person?: PersonList) => {
    if (!person) return

    formInstance.setFieldValue(
      [personsProps.name, name],
      mapExistingPerson(person)
    )
  }

  const uniqueIdnp = {
    validator: (_: unknown, value: string) =>
      countIdnpMatches(persons, value) > 1
        ? Promise.reject(new Error(t('duplicate_idnp_error')))
        : Promise.resolve()
  }

  return (
    <Spin spinning={isCalculatingLoading}>
      <ContractorTypeFields minAge={MIN_AGE} disableContractorType resident />

      <Divider className="mt-0" />

      <Row gutter={12}>
        <Col xs={24} lg={12}>
          <Form.Item
            {...territoryProps}
            rules={[required]}
            normalize={(value) => value?.label}
          >
            <CountrySelect placeholder={territoryProps.label} labelInValue />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <CoefficientInput onChange={handleRecalculate} />
        </Col>
      </Row>

      <Divider className="mt-0" />

      <Typography style={{ paddingBottom: 12 }}>
        {t('insured_persons')}
      </Typography>

      <EditPersonsWrapper
        personsKey={personsProps.name}
        showAddAction
        showDeleteAction={() => persons.length > 1}
        addIcon={<Add size={16} />}
        onAdd={syncPersonsCountAndRecalculate}
        onRemove={syncPersonsCountAndRecalculate}
        extraActions={(field) => (
          <AddPersonButton
            personType="person"
            multiple={false}
            type="primary"
            icon={<UserAdd size={16} />}
            onAdd={(person) => handleReplacePerson(field.name, person)}
          />
        )}
      >
        {(field) => (
          <Row gutter={12}>
            <Col xs={24} lg={12}>
              <Form.Item
                label={t('last_name')}
                name={[field.name, 'last_name']}
                rules={[required, fullName]}
              >
                <Input placeholder={t('last_name')} />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                label={t('first_name')}
                name={[field.name, 'first_name']}
                rules={[required, fullName]}
              >
                <Input placeholder={t('first_name')} />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                label={t('date_of_birth')}
                name={[field.name, 'date_of_birth']}
                rules={[required]}
              >
                <InputDatePicker maxDate={dayjs()} />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                label={t('identification_number')}
                name={[field.name, 'identification_number']}
                rules={[required, personalCode, uniqueIdnp]}
              >
                <Input placeholder={t('identification_number')} />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                label={t('passport_series')}
                name={[field.name, 'document']}
                rules={[required, passportSeriesNumber]}
              >
                <Input placeholder={t('passport_series')} />
              </Form.Item>
            </Col>
          </Row>
        )}
      </EditPersonsWrapper>
    </Spin>
  )
}
